import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState, useEffect } from "react";
import { 
  Users, 
  Eye, 
  Clock, 
  Smartphone, 
  Monitor, 
  Tablet,
  Chrome,
  RefreshCw,
  TrendingUp,
  Calendar,
  Globe,
  BarChart3
} from "lucide-react";

interface AnalyticsStats {
  period: number;
  totalVisits: number;
  uniqueVisitors: number;
  uniqueSessions: number;
  avgDuration: number;
  pageViews: Record<string, number>;
  deviceBreakdown: Record<string, number>;
  browserBreakdown: Record<string, number>;
  dailyStats: Array<{
    date: string;
    visits: number;
    uniqueVisitors: number;
    pageViews: number;
  }>;
  lastUpdated: string;
}

const SiteAnalytics = () => {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [period, setPeriod] = useState('7');
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      console.log(`Loading analytics for ${period} days...`);
      
      const { data, error } = await supabase.functions.invoke('site_analytics_20251219_1900', {
        method: 'GET',
        body: { period }
      });
      
      if (error) {
        console.error('Analytics error:', error);
        throw error;
      }
      
      console.log('Analytics data loaded:', data);
      setStats(data);
      
    } catch (error: any) {
      console.error('Failed to load analytics:', error);
      toast({
        title: "Erreur de chargement",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDeviceIcon = (device: string) => {
    switch (device.toLowerCase()) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      case 'desktop': return <Monitor className="h-4 w-4" />;
      default: return <Globe className="h-4 w-4" />;
    }
  };

  const formatPageUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.hash;
    } catch {
      return url;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Statistiques du Site</h1>
              <p className="text-muted-foreground">
                Analyse du trafic et des visiteurs de votre site web
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Période" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Dernières 24h</SelectItem>
                  <SelectItem value="7">7 derniers jours</SelectItem>
                  <SelectItem value="30">30 derniers jours</SelectItem>
                  <SelectItem value="90">90 derniers jours</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="sm"
                onClick={loadStats}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Chargement des statistiques...</p>
            </div>
          ) : !stats ? (
            <Card>
              <CardContent className="p-8 text-center">
                <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucune donnée disponible</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Visiteurs uniques</p>
                        <p className="text-2xl font-bold">{stats.uniqueVisitors}</p>
                      </div>
                      <Users className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Total visites</p>
                        <p className="text-2xl font-bold">{stats.totalVisits}</p>
                      </div>
                      <Eye className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Sessions</p>
                        <p className="text-2xl font-bold">{stats.uniqueSessions}</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Durée moyenne</p>
                        <p className="text-2xl font-bold">{formatDuration(stats.avgDuration)}</p>
                      </div>
                      <Clock className="h-8 w-8 text-orange-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Daily Stats Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Évolution quotidienne
                    </CardTitle>
                    <CardDescription>
                      Visiteurs et visites par jour
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {stats.dailyStats.map((day) => (
                        <div key={day.date} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium">
                              {formatDate(day.date)}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3 text-blue-500" />
                              <span>{day.uniqueVisitors}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Eye className="h-3 w-3 text-green-500" />
                              <span>{day.visits}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Pages */}
                <Card>
                  <CardHeader>
                    <CardTitle>Pages les plus visitées</CardTitle>
                    <CardDescription>
                      Répartition du trafic par page
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(stats.pageViews)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 10)
                        .map(([page, views]) => (
                        <div key={page} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {formatPageUrl(page)}
                            </p>
                          </div>
                          <Badge variant="secondary">
                            {views} vues
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Device Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Smartphone className="h-5 w-5" />
                      Appareils utilisés
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(stats.deviceBreakdown)
                        .sort(([,a], [,b]) => b - a)
                        .map(([device, count]) => (
                        <div key={device} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            {getDeviceIcon(device)}
                            <span className="capitalize">{device}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {count} visites
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {Math.round((count / stats.totalVisits) * 100)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Browser Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Chrome className="h-5 w-5" />
                      Navigateurs utilisés
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(stats.browserBreakdown)
                        .sort(([,a], [,b]) => b - a)
                        .map(([browser, count]) => (
                        <div key={browser} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Chrome className="h-4 w-4" />
                            <span>{browser}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {count} visites
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {Math.round((count / stats.totalVisits) * 100)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Last Updated */}
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Dernière mise à jour : {new Date(stats.lastUpdated).toLocaleString('fr-FR')}
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SiteAnalytics;